// features/projects/templates/professional/tokens.js
// ═══════════════════════════════════════════════════════════
// PROFESSIONAL TEMPLATE — OBSERVATORY THEME
//
// Locked with Tim 2026-05-03. The Professional template carries
// "the frontier of new knowledge" — distinct from Continuum
// (cool data room) and Strategic Playbook (oak archive).
// Observatory feels like deep-sky precision instruments at 3am
// from a mountaintop: warm metals against deep night, restrained,
// patient gravitas.
//
// Palette is semantic + thematic. Existing widget code references
// the semantic names (gold/sage/terra/steel) — those keep working,
// they just feel different now. New code can reach for the more
// specific aliases (brass, copper, silverOxide, moonglow, graphRule).
//
// Per CLAUDE.md "Domain-Theme Exception": each domain may define
// its own theme palette scoped to that domain's UI. Observatory
// applies to features/projects/templates/professional/* only;
// the cross-domain shell, sidebar, and chat threads stay on the
// canonical 7-color brand palette.
// ═══════════════════════════════════════════════════════════

export const L = {
  // Observatory surfaces — deep night, instrument warmth
  bg:    '#0a0e1c',  // observatory-night — the sky at 3am from a mountaintop
  s1:    '#13182a',  // observatory-deep — slightly elevated
  s2:    '#1a2138',  // observatory-card — instrument-on-desk surface
  s3:    '#222a45',  // raised controls

  // Borders — graph-paper hairlines + structural rules
  b1:    '#1f2a47',  // graph-rule — barely-visible alignment
  b2:    '#2a3658',  // stronger divider
  b3:    '#3a4a72',  // strongest

  // Text — warm whites, like reading by lantern
  text:  '#e8eaf2',  // moonglow — body text
  mid:   '#a4adba',  // silver-oxide — secondary text, instrument patina
  static:'#7d8696',  // dim silver — tertiary
  dim:   '#5a6376',  // graphite — captions, labels

  // 4-color semantic accents (Observatory-tuned)
  // Same names the widgets already reference; values shifted to
  // fit warm-metal-on-night palette.
  steel: '#6b8fa8',  // calibration / data structure
  sage:  '#7fa68a',  // supported / positive verdict
  gold:  '#b8956a',  // brass — your output / call to action
  terra: '#c4623f',  // copper — alert / contradiction / killed

  // Specific Observatory aliases (use these in new code for clarity)
  brass:        '#b8956a',  // = gold; the warm precision-metal accent
  copper:       '#c4623f',  // = terra; oxidized warning metal
  silverOxide:  '#a4adba',  // = mid; instrument-patina text
  moonglow:     '#e8eaf2',  // = text; warm-white body
  graphRule:    '#1f2a47',  // = b1; hairline alignment
  observatoryNight: '#0a0e1c',  // = bg
};

export const FONT = {
  mono:  "'DM Mono', monospace",
  sans:  "'DM Sans', sans-serif",
  serif: "'Cormorant Garamond', serif",
};

// Page-level decoration — subtle graph grid on the bg, like the
// orthogonal lines on a navigation chart. Fades into the night
// at the edges of the viewport. Apply via the top-level shell's
// background. 16px grid; very low opacity.
export const GRAPH_BACKDROP = `
  linear-gradient(${'#1f2a47'}26 1px, transparent 1px),
  linear-gradient(90deg, ${'#1f2a47'}26 1px, transparent 1px),
  ${'#0a0e1c'}
`;

// Corner-tick character set for instrument-frame card decoration.
// Brass at low opacity in the 4 corners of a card evokes drafting-
// table marks + telescope finder-scope frames.
export const CORNER_TICKS = {
  tl: '┌', tr: '┐',
  bl: '└', br: '┘',
};
